import React from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { api, formatApiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { UploadCloud, FileVideo } from "lucide-react";

const CATEGORIES = ["ugc", "cinematic", "demo", "lifestyle", "fashion", "fitness", "food", "tech", "general"];
const VERTICALS = ["ecommerce", "saas", "consumer", "beauty", "automotive", "general"];

export default function CreatorUpload() {
    const navigate = useNavigate();
    const [form, setForm] = React.useState({
        title: "",
        description: "",
        category: "ugc",
        vertical: "general",
        tags: "",
    });
    const [file, setFile] = React.useState(null);
    const [thumb, setThumb] = React.useState(null);
    const [uploading, setUploading] = React.useState(false);
    const [progress, setProgress] = React.useState(0);

    const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!file) return toast.error("Please pick a video");
        setUploading(true);
        setProgress(0);
        const fd = new FormData();
        Object.entries(form).forEach(([k, v]) => fd.append(k, v));
        fd.append("file", file);
        if (thumb) fd.append("thumbnail", thumb);
        try {
            await api.post("/videos/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                onUploadProgress: (e) => {
                    if (e.total) setProgress(Math.round((e.loaded * 100) / e.total));
                },
            });
            toast.success("Video uploaded");
            navigate("/creator");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="min-h-screen bg-white text-slate-900" data-testid="creator-upload">
            <Navbar />
            <section className="max-w-3xl mx-auto px-5 lg:px-8 py-12">
                <div className="chip chip-coral inline-flex mb-3">UPLOAD</div>
                <h1 className="font-display text-4xl font-semibold tracking-tight mb-8">
                    New video
                </h1>
                <form onSubmit={onSubmit} className="space-y-6 card-ecom p-6 lg:p-8" data-testid="upload-form">
                    <div className="space-y-2">
                        <Label className="text-sm font-medium">Title</Label>
                        <Input
                            required
                            value={form.title}
                            onChange={onChange("title")}
                            className="rounded-xl border-slate-200 h-11 focus-visible:ring-coral focus-visible:ring-2"
                            data-testid="upload-title"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label className="text-sm font-medium">Description</Label>
                        <Textarea
                            value={form.description}
                            onChange={onChange("description")}
                            rows={4}
                            className="rounded-xl border-slate-200 focus-visible:ring-coral focus-visible:ring-2"
                            data-testid="upload-description"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label className="text-sm font-medium">Category</Label>
                            <Select
                                value={form.category}
                                onValueChange={(v) => setForm({ ...form, category: v })}
                            >
                                <SelectTrigger
                                    className="rounded-xl border-slate-200 h-11"
                                    data-testid="upload-category"
                                >
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="rounded-xl">
                                    {CATEGORIES.map((c) => (
                                        <SelectItem key={c} value={c}>
                                            {c}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label className="text-sm font-medium">Vertical</Label>
                            <Select
                                value={form.vertical}
                                onValueChange={(v) => setForm({ ...form, vertical: v })}
                            >
                                <SelectTrigger
                                    className="rounded-xl border-slate-200 h-11"
                                    data-testid="upload-vertical"
                                >
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="rounded-xl">
                                    {VERTICALS.map((c) => (
                                        <SelectItem key={c} value={c}>
                                            {c}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label className="text-sm font-medium">Tags (comma separated)</Label>
                        <Input
                            value={form.tags}
                            onChange={onChange("tags")}
                            className="rounded-xl border-slate-200 h-11 focus-visible:ring-coral focus-visible:ring-2"
                            placeholder="cinematic, ugc, beauty"
                            data-testid="upload-tags"
                        />
                    </div>

                    <label className="block border-2 border-dashed border-slate-200 rounded-2xl p-8 text-center cursor-pointer hover:border-coral hover:bg-coral/5 transition-colors" data-testid="video-dropzone">
                        <UploadCloud className="w-9 h-9 text-coral mx-auto mb-3" />
                        <div className="text-sm font-medium text-slate-700">
                            {file ? file.name : "Pick video file (under 200MB)"}
                        </div>
                        <input
                            type="file"
                            accept="video/*"
                            className="hidden"
                            onChange={(e) => setFile(e.target.files?.[0] || null)}
                            data-testid="video-file-input"
                        />
                    </label>

                    <label className="block border border-dashed border-slate-200 rounded-2xl p-5 text-center cursor-pointer hover:border-slate-300 transition-colors" data-testid="thumb-dropzone">
                        <FileVideo className="w-5 h-5 text-slate-400 mx-auto mb-2" />
                        <div className="text-xs text-slate-500">
                            {thumb ? thumb.name : "Optional thumbnail"}
                        </div>
                        <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => setThumb(e.target.files?.[0] || null)}
                            data-testid="thumb-file-input"
                        />
                    </label>

                    {uploading && (
                        <div className="space-y-2" data-testid="upload-progress">
                            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                <div
                                    className="h-1.5 bg-coral rounded-full transition-all"
                                    style={{ width: `${progress}%` }}
                                />
                            </div>
                            <div className="text-xs text-slate-500">Uploading {progress}%</div>
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={uploading}
                        className="btn-pill-primary"
                        data-testid="upload-submit"
                    >
                        {uploading ? "Uploading…" : "Publish video →"}
                    </button>
                </form>
            </section>
        </div>
    );
}
